import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';

export interface DropdownItem {
  label: string;
  icon?: ReactNode;
  onClick: () => void | Promise<void>;
  disabled?: boolean;
  danger?: boolean;
  hint?: string;
}

interface Props {
  label: ReactNode;
  icon?: ReactNode;
  items: DropdownItem[];
  align?: 'left' | 'right';
  buttonClassName?: string;
  disabled?: boolean;
}

export default function DropdownMenu({
  label,
  icon,
  items,
  align = 'right',
  buttonClassName = 'toolbar-btn',
  disabled,
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative inline-block">
      <button type="button" className={buttonClassName} onClick={() => setOpen((o) => !o)} disabled={disabled}>
        {icon}
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          className={`absolute z-50 mt-1 min-w-[14rem] overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-2xl dark:border-slate-800 dark:bg-slate-950 ${
            align === 'right' ? 'right-0' : 'left-0'
          }`}
        >
          {items.map((it, i) => (
            <button
              key={i}
              type="button"
              disabled={it.disabled}
              onClick={async () => {
                setOpen(false);
                await it.onClick();
              }}
              title={it.hint}
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition ${
                it.disabled
                  ? 'cursor-not-allowed text-neutral-400 dark:text-neutral-600'
                  : it.danger
                  ? 'text-rose-700 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-500/10'
                  : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-900'
              }`}
            >
              {it.icon}
              <span className="flex-1">{it.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
